import type {
  AddParticipantBody,
  BoardSummary,
  BoardWithTasks,
  CreateBoardBody,
  CreateColumnBody,
  CreateTaskBody,
  TaskChecklistItem,
  TaskStage,
  TaskSummary,
  UpdateBoardBody,
  UpdateTaskBody,
} from '@obras/shared';
import { PERSONAL_BOARD_ID } from '@obras/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, queryString } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';

export const taskKeys = {
  all: ['tarefas'] as const,
  boards: ['tarefas', 'quadros'] as const,
  board: (boardId: string) => ['tarefas', 'quadro', boardId] as const,
};

export function useBoards() {
  return useQuery({
    queryKey: taskKeys.boards,
    queryFn: ({ signal }) => api.get<{ items: BoardSummary[] }>('/tarefas/quadros', signal),
  });
}

export function useBoard(boardId: string) {
  return useQuery({
    queryKey: taskKeys.board(boardId),
    queryFn: ({ signal }) =>
      api.get<BoardWithTasks>(
        `/tarefas${queryString({ boardId: boardId === PERSONAL_BOARD_ID ? undefined : boardId })}`,
        signal,
      ),
    placeholderData: (previous) => previous,
  });
}

/** Toda mutação de tarefa recarrega o quadro aberto e a contagem da lista. */
function useTaskMutation<TArgs, TResult>(
  boardId: string,
  mutationFn: (args: TArgs) => Promise<TResult>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: taskKeys.board(boardId) });
      void queryClient.invalidateQueries({ queryKey: taskKeys.boards });
    },
  });
}

export function useCreateTask(boardId: string) {
  return useTaskMutation(boardId, (body: CreateTaskBody) =>
    api.post<TaskSummary>('/tarefas', body),
  );
}

/**
 * Aplica o movimento na lista em memória, como o servidor aplicaria.
 *
 * A coluna de destino é reconstruída inteira com posições sequenciais; a de
 * origem só perde a tarefa e fica com buracos, o que não incomoda porque a
 * ordenação é relativa. É a mesma conta que o serviço faz — se as duas
 * divergirem, a releitura que vem depois corrige.
 */
function reorder(
  tasks: readonly TaskSummary[],
  isPersonal: boolean,
  taskId: string,
  target: string,
  position: number,
): TaskSummary[] {
  const moving = tasks.find((task) => task.id === taskId);
  if (!moving) return [...tasks];

  const columnOf = (task: TaskSummary): string | null => (isPersonal ? task.stage : task.columnId);

  const placed: TaskSummary = isPersonal
    ? { ...moving, stage: target as TaskStage, columnId: null }
    : { ...moving, columnId: target, stage: null };

  const destination = tasks
    .filter((task) => task.id !== taskId && columnOf(task) === target)
    .sort((a, b) => a.position - b.position);

  destination.splice(Math.min(position, destination.length), 0, placed);

  const positions = new Map(destination.map((task, index) => [task.id, index]));

  return tasks.map((task) => {
    if (task.id === taskId) return { ...placed, position: positions.get(taskId) ?? 0 };
    if (columnOf(task) === target)
      return { ...task, position: positions.get(task.id) ?? task.position };
    return task;
  });
}

type UpdateTaskVars = UpdateTaskBody & { id: string };

/** Só movimento merece antecipação; edição de texto fecha o modal e recarrega. */
const isMove = (vars: UpdateTaskVars): boolean =>
  vars.stage !== undefined || vars.columnId !== undefined || vars.position !== undefined;

/**
 * Mover cartão é otimista.
 *
 * O cartão troca de coluna no mesmo quadro em que o dedo soltou, sem esperar a
 * resposta — arrastar e ver o cartão voltar até o servidor confirmar é o que
 * fazia o quadro parecer travado. Se a requisição falhar, o estado anterior é
 * restaurado e a releitura mostra a verdade.
 */
export function useUpdateTask(boardId: string) {
  const queryClient = useQueryClient();
  const key = taskKeys.board(boardId);

  return useMutation({
    mutationFn: ({ id, ...body }: UpdateTaskVars) => api.patch<TaskSummary>(`/tarefas/${id}`, body),

    onMutate: async (vars) => {
      if (!isMove(vars)) return { previous: undefined };

      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<BoardWithTasks>(key);
      if (!previous) return { previous };

      const target = previous.board.isPersonal ? vars.stage : vars.columnId;
      const current = previous.tasks.find((task) => task.id === vars.id);
      const fallback = previous.board.isPersonal ? current?.stage : current?.columnId;
      const destination = target ?? fallback;
      if (!destination) return { previous };

      queryClient.setQueryData<BoardWithTasks>(key, {
        ...previous,
        tasks: reorder(
          previous.tasks,
          previous.board.isPersonal,
          vars.id,
          destination,
          vars.position ?? previous.tasks.length,
        ),
      });

      return { previous };
    },

    onError: (_error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },

    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: key });
      void queryClient.invalidateQueries({ queryKey: taskKeys.boards });
    },
  });
}

export function useDeleteTask(boardId: string) {
  return useTaskMutation(boardId, (id: string) => api.delete<void>(`/tarefas/${id}`));
}

export function useAddParticipant(boardId: string) {
  return useTaskMutation(boardId, ({ taskId, userId }: AddParticipantBody & { taskId: string }) =>
    api.post<TaskSummary>(`/tarefas/${taskId}/participantes`, { userId }),
  );
}

export function useRemoveParticipant(boardId: string) {
  return useTaskMutation(boardId, ({ taskId, userId }: { taskId: string; userId: string }) =>
    api.delete<TaskSummary>(`/tarefas/${taskId}/participantes/${userId}`),
  );
}

/** Marcar item também é otimista: a caixa não pode piscar esperando a rede. */
export function useToggleChecklistItem(boardId: string) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const key = taskKeys.board(boardId);

  return useMutation({
    mutationFn: ({ taskId, itemId, isDone }: { taskId: string; itemId: string; isDone: boolean }) =>
      api.patch<TaskSummary>(`/tarefas/${taskId}/checklist/${itemId}`, { isDone }),

    onMutate: async ({ taskId, itemId, isDone }) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<BoardWithTasks>(key);
      if (!previous) return { previous };

      /*
       * A autoria também é antecipada: quem acabou de marcar precisa ver o
       * próprio nome na hora, senão o item parece ter sido marcado por
       * ninguém até a releitura chegar.
       */
      const marked = (item: TaskChecklistItem): TaskChecklistItem => ({
        ...item,
        isDone,
        doneBy: isDone && user ? { id: user.id, name: user.name, avatarUrl: user.avatarUrl } : null,
        doneAt: isDone ? new Date().toISOString() : null,
        canToggle: true,
      });

      queryClient.setQueryData<BoardWithTasks>(key, {
        ...previous,
        tasks: previous.tasks.map((task) =>
          task.id === taskId
            ? {
                ...task,
                checklist: task.checklist.map((item) => (item.id === itemId ? marked(item) : item)),
              }
            : task,
        ),
      });

      return { previous };
    },

    onError: (_error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },

    onSettled: () => queryClient.invalidateQueries({ queryKey: key }),
  });
}

export function useCreateBoard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CreateBoardBody) => api.post<BoardSummary>('/tarefas/quadros', body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: taskKeys.boards }),
  });
}

export function useUpdateBoard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...body }: UpdateBoardBody & { id: string }) =>
      api.patch<{ ok: true }>(`/tarefas/quadros/${id}`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: taskKeys.all }),
  });
}

/** Vazio, o servidor apaga de vez; com tarefa, arquiva. Ver `removeBoard`. */
export function useRemoveBoard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/tarefas/quadros/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: taskKeys.all }),
  });
}

export function useAddBoardMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ boardId, userId }: { boardId: string; userId: string }) =>
      api.post<{ ok: true }>(`/tarefas/quadros/${boardId}/membros`, { userId }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: taskKeys.boards }),
  });
}

export function useRemoveBoardMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ boardId, userId }: { boardId: string; userId: string }) =>
      api.delete<void>(`/tarefas/quadros/${boardId}/membros/${userId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: taskKeys.boards }),
  });
}

export function useCreateColumn(boardId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CreateColumnBody) =>
      api.post<{ ok: true }>(`/tarefas/quadros/${boardId}/colunas`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: taskKeys.all }),
  });
}

export function useDeleteColumn(boardId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (columnId: string) =>
      api.delete<void>(`/tarefas/quadros/${boardId}/colunas/${columnId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: taskKeys.all }),
  });
}
